import React, { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import { BlogArticleTile } from "@arctheme-components/blog/BlogArticleTile/BlogArticleTile";
import CarouselNavigation from "@arctheme-components/blocks/NavigationElements/CarouselNavigation";

const CarouselLayout = ({ initialArticles, settings }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const { globals } = settings;
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage] = useState(20);
  const [articles, setArticles] = useState(initialArticles || []);
  const [currentArticles, setCurrentArticles] = useState(
    initialArticles?.slice(0, articlesPerPage) || [],
  );

  const carouselRef = useRef(null);

  const { section, blocks } = settings;
  let flexDirection = "flex-row";
  let navArrowsPosition = isMobile
    ? section.nav_arrows_position_mobile
    : section.nav_arrows_position;

  switch (navArrowsPosition) {
    case "bottom":
      flexDirection = "flex-col";
      break;
  }

  const contentBlock = blocks?.find((block) => block.type === "content_block");

  useEffect(() => {
    const handleArticlesComplete = (event) => {
      setCurrentPage(1);
      setArticles(event.detail.articles);
    };

    const handleNextPage = (event) => {
      if (event.detail.type === "pagination") {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("blog-articles-complete", handleArticlesComplete);
    window.addEventListener("nextpage", handleNextPage);

    return () => {
      window.removeEventListener(
        "blog-articles-complete",
        handleArticlesComplete,
      );
      window.removeEventListener("nextpage", handleNextPage);
    };
  }, []);

  useEffect(() => {
    const indexOfLastArticle = currentPage * articlesPerPage;
    const newArticles = articles.slice(0, indexOfLastArticle);
    setCurrentArticles(newArticles);
  }, [currentPage, articles, articlesPerPage]);

  const contentBlockRender = () => (
    <div className="flex flex-col space-y-[16px]">
      <Paragraph
        text={contentBlock?.settings.title}
        type={isMobile ? 'sh5' : contentBlock?.settings.heading_type}
      />
      <Paragraph
        text={contentBlock?.settings.subtext}
        type={contentBlock?.settings.subtext_type}
      />
    </div>
  );

  const {
    is_bleed_carousel,
    padding_left_right_desktop,
    is_bleed_carousel_mobile,
    padding_left_right_mobile,
  } = settings.section;

  const paddingDesktop = is_bleed_carousel
    ? `lg:pr-[${padding_left_right_desktop}px]`
    : '';

  const paddingMobile = is_bleed_carousel_mobile
    ? `pr-[${padding_left_right_mobile}px]`
    : '';

  return (
    <div
      className={`blog-articles-carousel w-full color-${section.color_schema} space-y-[24px] lg:space-y-[48px]`}
    >
      <div
        className={`section-toprow flex flex-row justify-between items-center ${paddingDesktop} ${paddingMobile}`}
      >
        {contentBlock && contentBlock?.settings.position == "top" && (
          <div className={"section-toprow__details flex"}>
            {contentBlockRender()}
          </div>
        )}
        {!isMobile && section.nav_arrows_position == "top" && (
          <div className={"section-toprow__actions flex"}>
            <CarouselNavigation
              shiftPageDots={true}
              carouselRef={carouselRef}
              icons={globals.carousel_icons}
              size={globals.carousel_icons?.carousel_navigation_arrow_size}
            />
          </div>
        )}
      </div>

      <div
        className={`blog-articles carousel-item-container flex ${flexDirection} w-full space-[24px]`}
      >
        {contentBlock && contentBlock.settings.position == "left" && (
          <div className={"section-leftrow__details flex basis-[20%]"}>
            {contentBlockRender()}
          </div>
        )}

        {!isMobile && section.nav_arrows_position == "within" && (
          <div
            className={
              "section-toprow__actions flex absolute justify-between w-full top-[50%]"
            }
          >
            <CarouselNavigation
              shiftPageDots={true}
              carouselRef={carouselRef}
              spread={true}
              icons={globals.carousel_icons}
              size={globals.carousel_icons?.carousel_navigation_arrow_size}
            />
          </div>
        )}

        {currentArticles.length > 0 && (
          <div
            {...{
              "x-data": "extended_carousel",
              "x-init": `$nextTick(() => { initialise(true, false, false, false, false, 'test') })`, // Pass isMobile as the first parameter
              "x-ref": "carousel",
            }}
            className={`carousel col-start-1 row-start-1 article-items gap-x-[20px]`}
            ref={carouselRef}
          >
            {currentArticles.map((article, index) => (
              <div
                className="carousel-item mr-[8px] lg:mr-[24px] blog-articles__article article"
                key={index}
              >
                <BlogArticleTile
                  article={article}
                  show_excerpt={true}
                  settings={settings}
                  showContent={false}
                  showCategory={true}
                  show_read_time={section.show_read_time}
                />
              </div>
            ))}
          </div>
        )}
        {currentArticles.length <= 0 && (
          <div className={"blog-articles-empty"}>
            <Paragraph text={"There are no articles."} type={"b2"} />
          </div>
        )}

        {isMobile && section.nav_arrows_position_mobile == "bottom" && (
          <div
            className={
              "section-bottomrow flex flex-row justify-between items-center mt-[24px]"
            }
          >
            <CarouselNavigation
              shiftPageDots={true}
              carouselRef={carouselRef}
              icons={globals.carousel_icons}
              size={globals.carousel_icons?.carousel_navigation_arrow_size}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselLayout;
